/**
 * @fileoverview Bridges bandwidth state to PostHog's session_recording config.
 *
 * Lives as a separate module so that `analytics.ts` doesn't need to import
 * `BandwidthMonitor` directly and vice versa — each side owns its own concern,
 * and this file is the single place that decides "given current network
 * conditions, what should the session recording config look like".
 */

import type { SessionRecordingCanvasOptions } from 'posthog-js'
import posthog from 'posthog-js'
import { getBandwidthMonitor } from './BandwidthMonitor'

// Memoize the decision so every consumer (PostHog config, Pixi WebGL context
// setup) sees the same answer. If they disagreed — e.g. PostHog read 'fast'
// before a probe finished but the engine read 'slow' after — PostHog would
// try to capture a canvas whose drawing buffer wasn't preserved and produce
// blank frames. BandwidthMonitor's classification can change post-init when a
// fresh probe completes, hence the snapshot.
let cachedRecordCanvas: boolean | null = null

function decideRecordCanvas(): boolean {
  if (cachedRecordCanvas !== null) return cachedRecordCanvas
  const classification = getBandwidthMonitor().classification
  cachedRecordCanvas = classification !== 'slow'
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[SessionRecording] canvas recording: ${cachedRecordCanvas} (bandwidth=${classification})`)
  }
  return cachedRecordCanvas
}

/**
 * Returns the PostHog `captureCanvas` config to use for a new session.
 *
 * Canvas-heavy views (Canvas v3, set viewer, timeline preview) render via
 * WebGL/Pixi, which rrweb can't capture without `recordCanvas`. Frame capture
 * is bandwidth-heavy, so we opt out when we already know the user is on a slow
 * connection. BandwidthMonitor's constructor restores the prior classification
 * from localStorage synchronously, so this works without waiting for a fresh
 * probe; first-time visitors are 'unknown' and opt in.
 *
 * The decision is fixed for the session — PostHog can't toggle `recordCanvas`
 * mid-session. A user whose connection changes mid-session keeps the policy
 * they started with until next page load.
 */
export function getInitialCanvasRecordingConfig(): SessionRecordingCanvasOptions {
  return {
    recordCanvas: decideRecordCanvas(),
    canvasFps: 3,
    canvasQuality: '0.4',
  }
}

/**
 * Whether canvas recording is active for the current session. Consumers other
 * than the PostHog init (e.g. the Pixi engine, which needs `preserveDrawingBuffer`
 * to support async `toDataURL` reads from rrweb) call this to stay in lockstep
 * with the recording config — avoiding paying the cost of buffer retention when
 * we already opted the user out of canvas recording.
 */
export function isCanvasRecordingEnabled(): boolean {
  return decideRecordCanvas()
}

/**
 * Runtime check for whether PostHog is actively recording this tab. The
 * bandwidth policy above answers "may canvas capture run"; this answers
 * "is a replay currently being collected". Keeping the two separate lets
 * expensive replay-only helpers stay idle for sessions that are eligible
 * for recording but not sampled by PostHog.
 */
export function isSessionReplayRecordingActive(): boolean {
  try {
    return posthog.sessionRecordingStarted() === true
  } catch {
    return false
  }
}
